"use client"

import React, {useEffect, useRef, useState} from "react";
import {getAltText, IReactiveMediaContentItem} from "./SchoolFeedContentItem";
import styles from "./styles/SchoolFeedMediaDisplay.module.scss"
import Vibrant from 'node-vibrant';
import widgetStyles from "./styles/SchoolFeedWidget.module.scss";
import Button from "../utilities/Button";
import FontAwesome from "../utilities/FontAwesome";
import {observer} from "mobx-react";
import {CSSTransition} from "react-transition-group";
import {ISchoolFeedUrlQueries} from "../../utils/SchoolBlocksUtilities";
import {InitialLoadingIndicatorWrapper} from "./InitialLoadingIndicatorWrapper";
import {useSchoolFeedInfiniteScroll} from "./hooks/useSchoolFeedInfiniteScroll";
import {SocialItemModalNavigationProps} from "../modals/SocialItemModal";
import {handleContentItemClick} from "../blocks/SchoolBlocks/ContentItemBlock/ContentItemBlock";
import {EditButton} from "../blocks/TerracedGridTheme/EditButton";
import classNames from 'classnames';
import ImageComponent from "../utilities/ImageComponent";
import {useStores} from "../../stores/useStores";
import {deleteContentItem} from "../blocks/TerracedGridTheme/Terrace";

const visibilityTransitionClassNames = {
    enter: styles.transitionEnter,
    appear: styles.transitionAppear,
    enterActive: styles.transitionEnterActive,
    appearActive: styles.transitionAppearActive,
    enterDone: styles.transitionEnterDone,
    appearDone: styles.transitionAppearDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};
const Media = observer((props: {
    item: IReactiveMediaContentItem,
    handleDeleteContentItem: (c: ContentItem) => void,
} & SocialItemModalNavigationProps) => {
    const {userStore} = useStores();
    const ref = useRef(null);
    const imgSource = props.item.image || "";
    const [bgColor, setBgColor] = useState<string | null>(null);

    useEffect(() => {
        if (imgSource) {
            Vibrant.from(imgSource)
                .quality(1)
                .getPalette((err, palette) => {
                    let color = palette?.LightMuted
                        || palette?.DarkMuted
                        || palette?.LightVibrant
                        || palette?.DarkVibrant
                        || palette?.Vibrant
                        || palette?.Muted;

                    if (color) {
                        setBgColor(`rgb(${color.r}, ${color.g}, ${color.b})`)
                    }
                });
        }
    }, [])

    const mediaItemClassName = classNames({
        [styles.mediaItem]: true,
        [styles.mediaItemHidden]: !props.item.published,
    });

    return <CSSTransition classNames={visibilityTransitionClassNames} timeout={500} in={true} appear={true} nodeRef={ref}>
        <div ref={ref} style={{backgroundColor: bgColor || "", cursor: "pointer"}} onClick={() => {
            handleContentItemClick(props.item, {
                totalItemCount: props.totalItemCount,
                initialItemIndex: props.initialItemIndex,
                getItemAtIndex: props.getItemAtIndex,
            }, props.handleDeleteContentItem)
        }}>
            <div className={mediaItemClassName}>
                <ImageComponent key={props.item.id}
                     src={imgSource}
                     alt={getAltText(props.item)}/>
                {userStore.isEditor &&
                    <EditButton
                        contentItem={props.item}
                        handleDelete={props.handleDeleteContentItem}
                        className={styles.editButton}
                    />}
            </div>
        </div>
    </CSSTransition>
})

export type SchoolFeedMediaDisplayProps = {
    urlQuery: ISchoolFeedUrlQueries,
}

export const SchoolFeedMediaDisplay = observer((props: SchoolFeedMediaDisplayProps) => {
    const {
        contentItems,
        showViewMore,
        isLoading,
        isValidating,
        size,
        setSize,
        error,
        totalContentItems,
        getItemAtIndex,
        mutate,
    } = useSchoolFeedInfiniteScroll(
        "media",
        {
            is_media: true,
        }
    )

    async function handleDeleteContentItem(contentItem: IReactiveMediaContentItem) {
        await deleteContentItem(contentItem);
        await mutate();
    }

    return <InitialLoadingIndicatorWrapper
        initialFetchIsLoading={contentItems.length === 0 && isLoading}
        dataSize={contentItems.length}
        errorCode={error?.status}
        showInitialLoadingIndicator={true}
    >
        <div className={`${styles.container} sb-content-width`}>
            <div className={styles.content}>
                {contentItems.map((item, idx) => <Media
                    key={item.id}
                    item={item as IReactiveMediaContentItem}
                    getItemAtIndex={getItemAtIndex}
                    totalItemCount={totalContentItems}
                    initialItemIndex={idx}
                    handleDeleteContentItem={handleDeleteContentItem}
                />)}
            </div>
            {((showViewMore && process.env.NEXT_PUBLIC_APP_CTX !== "schoolfeed")) &&
                <div style={{marginTop: "2rem"}} className={widgetStyles.fetchMoreContainer}>
                    <Button disabled={isValidating}
                            onClick={() => setSize(size + 1)}>{isValidating ?
                        <FontAwesome name={'fas fa-spinner'} spin/> : "View More"}</Button></div>}
        </div>
    </InitialLoadingIndicatorWrapper>
})
